import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { formatKst } from "@/lib/datetime";
import { formatWon } from "@/lib/quote";
import type {
  InquiryDTO,
  InquiryListResponse,
  InquiryProgress,
  InquiryStatus,
  QuoteSnapshotDTO,
} from "@/types/inquiry";

const PAGE_SIZE = 20;

const STATUS_FILTERS: { value: InquiryStatus | ""; label: string }[] = [
  { value: "", label: "전체" },
  { value: "new", label: "신규" },
  { value: "read", label: "읽음" },
  { value: "archived", label: "보관" },
];

const STATUS_LABEL: Record<InquiryStatus, string> = { new: "신규", read: "읽음", archived: "보관" };

/** 게시판(InquiryBoardPage)과 같은 라벨을 써요 — 어드민이 고른 게 그대로 고객에게 보여요. */
const PROGRESS_LABEL: Record<InquiryProgress, string> = {
  received: "접수",
  in_progress: "처리중",
  done: "완료",
};

/** 새 스냅샷은 라벨을 얼려 담고, 2026-07-25 이전 문의는 키만 있어요. */
const typeLabel = (quote: QuoteSnapshotDTO) => quote.type_label ?? quote.type;

function optionSummary(quote: QuoteSnapshotDTO): string {
  if (quote.options.length > 0) {
    return quote.options.map((o) => `${o.label} (${formatWon(o.amount)})`).join(", ");
  }
  // 구 포맷: 키 목록 + 앱 동반 개발 불리언
  const legacy = [...(quote.features ?? []), ...(quote.with_mobile_app ? ["앱 동반 개발"] : [])];
  return legacy.join(", ") || "없음";
}

interface InquiryPatch {
  status?: InquiryStatus;
  progress?: InquiryProgress;
  is_public?: boolean;
}

function InquiryDetail({
  inquiry,
  onStatusChange,
  onPatch,
  onDelete,
}: {
  inquiry: InquiryDTO;
  onStatusChange: (status: InquiryStatus) => void;
  onPatch: (patch: InquiryPatch) => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-lg bg-background-elevated p-6 shadow-normal">
      <div className="flex items-center justify-between gap-2">
        <span className="wds-label-1 text-label-assistive">문의 상세</span>
        <span className="rounded-full bg-background-alternative px-3 py-1 wds-caption-1 font-semibold text-label-neutral">
          {STATUS_LABEL[inquiry.status]}
        </span>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 wds-body-2">
        <dt className="text-label-assistive">이름</dt>
        <dd className="text-label-normal">{inquiry.contact.name}</dd>
        <dt className="text-label-assistive">이메일</dt>
        <dd className="text-label-normal">{inquiry.contact.email}</dd>
        <dt className="text-label-assistive">연락처</dt>
        <dd className="text-label-normal">{inquiry.contact.phone || "-"}</dd>
      </dl>

      <div className="flex flex-col gap-1.5">
        <span className="wds-label-1 text-label-strong">문의 내용</span>
        <p className="wds-body-2-reading whitespace-pre-wrap text-label-normal">{inquiry.message}</p>
      </div>

      {inquiry.quote && (
        <div className="flex flex-col gap-1.5 border-line-alternative border-t pt-4">
          <span className="wds-label-1 text-label-strong">견적 스냅샷</span>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 wds-body-2">
            <dt className="text-label-assistive">유형</dt>
            <dd className="text-label-normal">{typeLabel(inquiry.quote)}</dd>
            <dt className="text-label-assistive">페이지 수</dt>
            <dd className="tabular-nums text-label-normal">{inquiry.quote.pages}</dd>
            <dt className="text-label-assistive">선택 옵션</dt>
            <dd className="text-label-normal">{optionSummary(inquiry.quote)}</dd>
            <dt className="text-label-assistive">월 지원</dt>
            <dd className="text-label-normal">{inquiry.quote.support_label ?? inquiry.quote.support}</dd>
            <dt className="text-label-assistive">할인가</dt>
            <dd className="tabular-nums text-label-normal">{formatWon(inquiry.quote.discounted)}</dd>
            <dt className="text-label-assistive">월 유지보수</dt>
            <dd className="tabular-nums text-label-normal">
              {formatWon(inquiry.quote.monthly)}
              {/* 롤링 배포 중 구 api 응답엔 이 필드가 없어요. */}
              {(inquiry.quote.monthly_free_months ?? 0) > 0 &&
                ` (첫 ${inquiry.quote.monthly_free_months}개월 무료)`}
            </dd>
            <dt className="text-label-assistive">단가 버전</dt>
            <dd className="tabular-nums text-label-normal">{inquiry.quote.config_version ?? "-"}</dd>
          </dl>
        </div>
      )}

      {inquiry.session_id && (
        <p className="wds-caption-1 border-line-alternative border-t pt-4 text-label-neutral">
          방문 세션 <span className="tabular-nums">{inquiry.session_id}</span> — 방문 분석 탭의 “문의한 방문만” 필터로
          경로를 찾을 수 있어요.
        </p>
      )}

      {/* 게시판에 보이는 값 — 어드민 내부 상태(status)와 별개로 움직여요. */}
      <div className="flex flex-wrap items-center gap-4 border-line-alternative border-t pt-4">
        <label className="flex items-center gap-2 wds-caption-1 text-label-neutral">
          처리 현황
          <select
            value={inquiry.progress ?? "received"}
            onChange={(e) => onPatch({ progress: e.target.value as InquiryProgress })}
            className="h-9 rounded-md border border-line-normal bg-background-normal px-2 wds-caption-1 text-label-normal"
          >
            {(Object.keys(PROGRESS_LABEL) as InquiryProgress[]).map((key) => (
              <option key={key} value={key}>
                {PROGRESS_LABEL[key]}
              </option>
            ))}
          </select>
        </label>
        {/* 체크가 곧 비공개예요 — 저장되는 값(is_public)과 반대라 바인딩에서 한 번 뒤집어요. */}
        <label className="flex items-center gap-2 wds-caption-1 text-label-neutral">
          <input
            type="checkbox"
            checked={!(inquiry.is_public ?? false)}
            onChange={(e) => onPatch({ is_public: !e.target.checked })}
          />
          비공개 <span className="text-label-assistive">(해제하면 누구나 본문까지 볼 수 있어요)</span>
        </label>
      </div>

      <div className="flex flex-wrap gap-2 border-line-alternative border-t pt-4">
        <button
          type="button"
          onClick={() => onStatusChange("read")}
          disabled={inquiry.status === "read"}
          className="h-9 rounded-md bg-background-alternative px-4 wds-caption-1 font-semibold text-label-neutral disabled:opacity-40"
        >
          읽음으로 표시
        </button>
        <button
          type="button"
          onClick={() => onStatusChange("archived")}
          disabled={inquiry.status === "archived"}
          className="h-9 rounded-md bg-background-alternative px-4 wds-caption-1 font-semibold text-label-neutral disabled:opacity-40"
        >
          보관하기
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="h-9 rounded-md bg-background-alternative px-4 wds-caption-1 font-semibold text-status-negative"
        >
          삭제
        </button>
      </div>
    </div>
  );
}

export function InquiryPanel({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | "">("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<InquiryListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const query = new URLSearchParams({ page: String(page), size: String(PAGE_SIZE) });
    if (statusFilter) query.set("status", statusFilter);
    try {
      const result = await api.get<InquiryListResponse>(`/admin/inquiries?${query}`, token);
      setData(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setError(err instanceof ApiError ? err.message : "목록을 불러오지 못했어요.");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, page, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  // PATCH 는 준 필드만 바꿔요 — status(어드민 내부)와 progress/is_public(게시판)이 서로를 덮지 않아요.
  const handlePatch = async (id: string, patch: InquiryPatch) => {
    try {
      await api.patch<InquiryDTO>(`/admin/inquiries/${id}`, patch, token);
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setError(err instanceof ApiError ? err.message : "상태를 변경하지 못했어요.");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("이 문의를 삭제할까요? 되돌릴 수 없어요.")) return;
    try {
      await api.del(`/admin/inquiries/${id}`, token);
      setSelectedId(null);
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setError(err instanceof ApiError ? err.message : "삭제하지 못했어요.");
    }
  };

  const selected = data?.items.find((item) => item._id === selectedId) ?? null;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => {
              setStatusFilter(f.value);
              setPage(1);
            }}
            className={`wds-label-1 rounded-md px-4 py-2 transition-colors duration-200 ease-standard ${
              statusFilter === f.value ? "bg-primary text-static-white" : "bg-background-alternative text-label-neutral"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <output className="wds-body-2 text-status-negative">{error}</output>}

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="overflow-x-auto rounded-lg bg-background-elevated shadow-normal">
          <table className="w-full wds-body-2">
            <thead>
              <tr className="border-line-alternative border-b text-label-assistive">
                <th className="px-4 py-3 text-left font-medium">생성일</th>
                <th className="px-4 py-3 text-left font-medium">이름</th>
                <th className="px-4 py-3 text-left font-medium">이메일</th>
                <th className="px-4 py-3 text-left font-medium">유형</th>
                <th className="px-4 py-3 text-right font-medium">할인가</th>
                <th className="px-4 py-3 text-left font-medium">상태</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((item) => (
                <tr
                  key={item._id}
                  tabIndex={0}
                  // biome-ignore lint/a11y/useSemanticElements: <tr> can't become a <button> and keep table semantics; keyboard support is added manually below.
                  role="button"
                  aria-pressed={selectedId === item._id}
                  onClick={() => setSelectedId(item._id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedId(item._id);
                    }
                  }}
                  className={`cursor-pointer border-line-alternative border-b transition-colors duration-200 ease-standard hover:bg-background-alternative ${
                    selectedId === item._id ? "bg-background-alternative" : ""
                  }`}
                >
                  <td className="px-4 py-3 text-label-neutral">
                    {formatKst(item.created_at)}
                  </td>
                  <td className="px-4 py-3 text-label-normal">
                    {item.contact.name}
                    {/* stage 시더가 넣은 합성 문의. prod 엔 시더가 없어 이 배지가 뜰 일이 없어요. */}
                    {item.source === "seed" && (
                      <span className="ml-1.5 rounded bg-background-alternative px-1.5 py-0.5 wds-caption-1 text-label-assistive">
                        합성
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-label-neutral">{item.contact.email}</td>
                  <td className="px-4 py-3 text-label-neutral">{item.quote ? typeLabel(item.quote) : "-"}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-label-normal">
                    {item.quote ? formatWon(item.quote.discounted) : "-"}
                  </td>
                  <td className="px-4 py-3 text-label-neutral">{STATUS_LABEL[item.status]}</td>
                </tr>
              ))}
              {!loading && data?.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-label-assistive">
                    문의가 없어요.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <span className="wds-caption-1 text-label-assistive">
              총 <span className="tabular-nums">{data?.total ?? 0}</span>건
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="h-8 rounded-md bg-background-alternative px-3 wds-caption-1 disabled:opacity-40"
              >
                이전
              </button>
              <span className="wds-caption-1 tabular-nums text-label-neutral">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="h-8 rounded-md bg-background-alternative px-3 wds-caption-1 disabled:opacity-40"
              >
                다음
              </button>
            </div>
          </div>
        </div>

        {selected && (
          <InquiryDetail
            inquiry={selected}
            onStatusChange={(status) => handlePatch(selected._id, { status })}
            onPatch={(patch) => handlePatch(selected._id, patch)}
            onDelete={() => handleDelete(selected._id)}
          />
        )}
      </div>
    </div>
  );
}

export default InquiryPanel;
